import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/Button'
import { Contrast, Eye, EyeOff } from 'lucide-react'

/**
 * High contrast mode context and provider
 */
export const HighContrastContext = React.createContext<{
  isHighContrast: boolean
  toggleHighContrast: () => void
  reducedMotion: boolean
  toggleReducedMotion: () => void
}>({
  isHighContrast: false,
  toggleHighContrast: () => {},
  reducedMotion: false,
  toggleReducedMotion: () => {}
})

interface HighContrastProviderProps {
  children: React.ReactNode
}

export function HighContrastProvider({ children }: HighContrastProviderProps) {
  const [isHighContrast, setIsHighContrast] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('msb-high-contrast') === 'true' ||
           window.matchMedia('(prefers-contrast: high)').matches
  })

  const [reducedMotion, setReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('msb-reduced-motion') === 'true' ||
           window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  const toggleHighContrast = () => {
    const newValue = !isHighContrast
    setIsHighContrast(newValue)
    localStorage.setItem('msb-high-contrast', newValue.toString())
  }

  const toggleReducedMotion = () => {
    const newValue = !reducedMotion
    setReducedMotion(newValue)
    localStorage.setItem('msb-reduced-motion', newValue.toString())
  }

  // Apply classes to document element
  useEffect(() => {
    const classes = []
    if (isHighContrast) classes.push('high-contrast')
    if (reducedMotion) classes.push('reduced-motion')

    document.documentElement.className = document.documentElement.className
      .replace(/\b(high-contrast|reduced-motion)\b/g, '')
      .trim()

    if (classes.length > 0) {
      document.documentElement.className += ` ${classes.join(' ')}`
    }
  }, [isHighContrast, reducedMotion])

  // Listen for system preference changes
  useEffect(() => {
    const contrastMediaQuery = window.matchMedia('(prefers-contrast: high)')
    const motionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    const handleContrastChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('msb-high-contrast') === null) {
        setIsHighContrast(e.matches)
      }
    }

    const handleMotionChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('msb-reduced-motion') === null) {
        setReducedMotion(e.matches)
      }
    }

    contrastMediaQuery.addEventListener('change', handleContrastChange)
    motionMediaQuery.addEventListener('change', handleMotionChange)

    return () => {
      contrastMediaQuery.removeEventListener('change', handleContrastChange)
      motionMediaQuery.removeEventListener('change', handleMotionChange)
    }
  }, [])

  const value = {
    isHighContrast,
    toggleHighContrast,
    reducedMotion,
    toggleReducedMotion
  }

  return (
    <HighContrastContext.Provider value={value}>
      {children}
    </HighContrastContext.Provider>
  )
}

/**
 * Hook to use high contrast context
 */
export function useHighContrast() {
  const context = React.useContext(HighContrastContext)
  if (!context) {
    throw new Error('useHighContrast must be used within HighContrastProvider')
  }
  return context
}

/**
 * High contrast toggle button
 */
interface HighContrastToggleProps {
  className?: string
  showLabel?: boolean
}

export function HighContrastToggle({ className = '', showLabel = true }: HighContrastToggleProps) {
  const { t } = useTranslation(['common'])
  const { isHighContrast, toggleHighContrast } = useHighContrast()

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggleHighContrast}
      className={`accessibility-toggle ${className}`}
      aria-label={isHighContrast 
        ? t('common:accessibility.disableHighContrast') 
        : t('common:accessibility.enableHighContrast')
      }
      title={isHighContrast 
        ? t('common:accessibility.disableHighContrast') 
        : t('common:accessibility.enableHighContrast')
      }
    >
      <Contrast className="h-4 w-4" aria-hidden="true" />
      {showLabel && (
        <span className="ms-2">
          {isHighContrast 
            ? t('common:accessibility.highContrastOn')
            : t('common:accessibility.highContrastOff')
          }
        </span>
      )}
    </Button>
  )
}

/**
 * Reduced motion toggle button
 */
interface ReducedMotionToggleProps {
  className?: string
  showLabel?: boolean
}

export function ReducedMotionToggle({ className = '', showLabel = true }: ReducedMotionToggleProps) {
  const { t } = useTranslation(['common'])
  const { reducedMotion, toggleReducedMotion } = useHighContrast()

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggleReducedMotion}
      className={`accessibility-toggle ${className}`}
      aria-label={reducedMotion 
        ? t('common:accessibility.enableMotion') 
        : t('common:accessibility.reduceMotion')
      }
      title={reducedMotion 
        ? t('common:accessibility.enableMotion') 
        : t('common:accessibility.reduceMotion')
      }
    >
      {reducedMotion ? (
        <EyeOff className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Eye className="h-4 w-4" aria-hidden="true" />
      )}
      {showLabel && (
        <span className="ms-2">
          {reducedMotion 
            ? t('common:accessibility.motionReduced')
            : t('common:accessibility.motionNormal')
          }
        </span>
      )}
    </Button>
  )
}

/**
 * Accessibility toolbar component
 */
interface AccessibilityToolbarProps {
  className?: string
}

export function AccessibilityToolbar({ className = '' }: AccessibilityToolbarProps) {
  const { t } = useTranslation(['common'])

  return (
    <div 
      className={`accessibility-toolbar flex items-center space-x-2 ${className}`}
      role="toolbar"
      aria-label={t('common:accessibility.accessibilityOptions')}
    >
      <HighContrastToggle showLabel={false} />
      <ReducedMotionToggle showLabel={false} />
    </div>
  )
}